import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Tags } from "lucide-react";
import toast from "react-hot-toast";
import { adminService } from "@/services/adminService";
import { Button, fieldControlClass } from "@/components/ui";
import { ACTIVITY_TYPE_ORDER, activityTypeLabel } from "@/constants";
import { toastApiError } from "@/lib/errorMessages";
import { ActivityCategory, type Activity } from "@/types";
import { StaffConfigRow } from "./StaffConfigRow";

interface AdminActivityCategorySectionProps {
  activity: Activity;
}

export function AdminActivityCategorySection({
  activity,
}: AdminActivityCategorySectionProps) {
  const { t } = useTranslation("activities");
  const { t: tCommon } = useTranslation("common");
  const queryClient = useQueryClient();
  const activityId = activity.id;

  const [editing, setEditing] = useState(false);
  const [draftCategory, setDraftCategory] = useState<ActivityCategory>(
    activity.category,
  );

  const updateConfigMutation = useMutation({
    mutationFn: (config: { category: ActivityCategory }) =>
      adminService.updateActivityConfig(activityId, config),
    onSuccess: async () => {
      toast.success(t("detail.adminCategoryToastSaved"));
      await queryClient.invalidateQueries({
        queryKey: ["activity", activityId],
      });
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      queryClient.invalidateQueries({
        queryKey: ["admin", "seller", activity.user_id, "activities"],
      });
      setEditing(false);
    },
    onError: (err) => {
      toastApiError(err, "updateActivity");
    },
  });

  return (
    <StaffConfigRow
      icon={Tags}
      title={t("detail.adminCategoryTitle")}
      hint={t("detail.adminCategoryHint")}
      actions={
        editing ? (
          <>
            <select
              value={draftCategory}
              onChange={(e) =>
                setDraftCategory(e.target.value as ActivityCategory)
              }
              className={fieldControlClass({
                size: "sm",
                className: "w-auto cursor-pointer",
              })}
              aria-label={t("detail.adminCategoryTitle")}
            >
              {ACTIVITY_TYPE_ORDER.map((cat) => (
                <option
                  key={cat}
                  value={cat}
                >
                  {activityTypeLabel(cat)}
                </option>
              ))}
            </select>
            <Button
              type="button"
              size="sm"
              onClick={() => {
                if (draftCategory === activity.category) {
                  setEditing(false);
                  return;
                }
                updateConfigMutation.mutate({ category: draftCategory });
              }}
              isLoading={updateConfigMutation.isPending}
            >
              {tCommon("save")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setDraftCategory(activity.category);
                setEditing(false);
              }}
            >
              {tCommon("cancel")}
            </Button>
          </>
        ) : (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              setDraftCategory(activity.category);
              setEditing(true);
            }}
          >
            {t("detail.adminCommissionEdit")}
          </Button>
        )
      }
    >
      {!editing ? (
        <span className="text-surface-900 text-sm font-semibold">
          {activityTypeLabel(activity.category)}
        </span>
      ) : null}
    </StaffConfigRow>
  );
}
